import { useState, useEffect } from "react";
import { LayoutDashboard, FileText, Palette, Paintbrush, Mail, Settings, LogOut, UserCircle, Shield, Link2, Crown, ExternalLink, Sparkles, Users, HelpCircle, ChevronsUpDown, Search, ArrowRight, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChatbotPanel } from "./ChatbotWidget";
import logoSrc from "@/assets/logo.svg";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/lib/auth";
import { useAdmin } from "@/hooks/use-admin";
import { useSubscription } from "@/hooks/use-subscription";
import { useSubscribeModal } from "./SubscribeModalProvider";
import { useAiCredits } from "@/hooks/use-ai-credits";
import { useNavigate, Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

const navItems = [
  { title: "Dashboard", url: "/app/dashboard", icon: LayoutDashboard },
  { title: "Landings", url: "/app/landings", icon: FileText },
  { title: "Templates", url: "/app/templates", icon: Palette },
  { title: "Leads", url: "/app/leads", icon: Users },
  { title: "Link in Bio", url: "/app/linkinbio", icon: ExternalLink },
  { title: "Ma marque", url: "/app/brand", icon: Paintbrush, pro: true },
  { title: "Générateur UTM", url: "/app/utm-generator", icon: Link2, pro: true },
  { title: "Intégration mail", url: "/app/integrations", icon: Mail },
];

export function AppSidebar() {
  const { signOut, user } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const isMobile = useIsMobile();
  const { setOpenMobile } = useSidebar();
  const { isPro, isTrialActive } = useSubscription();
  const { openSubscribe } = useSubscribeModal();
  const { subscriptionLoading } = useAuth();
  const { remaining, limit, loading: creditsLoading } = useAiCredits();
  const pct = limit > 0 ? (remaining / limit) * 100 : 0;
  const progressColor = pct <= 5 ? "[&>div]:bg-red-500" : pct <= 20 ? "[&>div]:bg-orange-400" : "";

  const [firstName, setFirstName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("first_name, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.first_name) setFirstName(data.first_name);
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
      });
  }, [user]);

  const email = user?.email ?? "";
  const displayName = firstName || email;
  const initials = firstName
    ? firstName.charAt(0).toUpperCase()
    : email.charAt(0).toUpperCase();

  const handleNavClick = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

    return (
    <>
      <Sidebar className="border-r border-sidebar-border">
        <div className="p-4">
          <Link to="/app/dashboard" onClick={handleNavClick}>
            <img src={logoSrc} alt="Magnetiq" className="h-5" width={98} height={20} />
          </Link>
        </div>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                        activeClassName="bg-sidebar-accent text-primary font-medium"
                        onClick={handleNavClick}
                      >
                        <item.icon className="h-4 w-4 sm:h-4 sm:w-4" />
                        <span className="flex-1 text-[15px] sm:text-sm">{item.title}</span>
                        {item.pro && !isPro && <Crown className="h-3.5 w-3.5 text-primary" />}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {isAdmin && (
                  <>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to="/app/seo"
                          className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                          activeClassName="bg-sidebar-accent text-primary font-medium"
                          onClick={handleNavClick}
                        >
                          <Search className="h-4 w-4" />
                          <span>SEO</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to="/app/admin"
                          className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                          activeClassName="bg-sidebar-accent text-primary font-medium"
                          onClick={handleNavClick}
                        >
                          <Shield className="h-4 w-4" />
                          <span>Admin</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          {!subscriptionLoading && isTrialActive && (
            <button
              onClick={() => { openSubscribe(); handleNavClick(); }}
              className="relative overflow-hidden flex items-center gap-2 mx-3 mt-2 px-4 py-2.5 bg-highlight text-highlight-foreground rounded-lg text-sm font-semibold hover:bg-highlight/90 transition-colors shadow-sm animate-shimmer-badge"
            >
              <Crown className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">S'abonner maintenant</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </button>
          )}
        </SidebarContent>
        <SidebarFooter className="p-2 space-y-1">
          {!creditsLoading && (
            <div className="px-3 py-2 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" />
                <span>IA</span>
                <span className="ml-auto font-medium">{remaining} / {limit}</span>
              </div>
              <Progress value={pct} className={`h-1.5 ${progressColor}`} />
            </div>
          )}
          <button
            onClick={() => { setChatOpen(true); handleNavClick(); }}
            className="flex items-center gap-3 mx-1 px-3 py-2 rounded-md text-sm text-sidebar-foreground border border-blue-400 hover:bg-sidebar-accent transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            <span>Assistant</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 px-3 py-2 w-full rounded-md text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors outline-none">
                <Avatar className="h-7 w-7">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt="Avatar" />}
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate leading-tight">{displayName}</p>
                  {firstName && <p className="text-xs text-muted-foreground truncate leading-tight">{email}</p>}
                </div>
                <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-[--radix-popper-anchor-width] bg-popover z-50">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{displayName}</p>
                  <p className="text-xs leading-none text-muted-foreground">{email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { navigate("/app/account"); handleNavClick(); }} className="cursor-pointer">
                <UserCircle className="mr-2 h-4 w-4" />
                Mon compte
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigate("/app/settings"); handleNavClick(); }} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Paramètres
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <a href="/aide" target="_blank" rel="noopener noreferrer">
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Aide
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="w-[95vw] max-w-lg h-[80vh] sm:h-[520px] p-0 flex flex-col gap-0">
          <DialogHeader className="px-4 py-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              Assistant Magnetiq
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <ChatbotPanel />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
